import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import InlineFeedback from '../components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (
      currentHost === 'localhost'
      || currentHost === '127.0.0.1'
      || currentHost === '::1'
      || currentHost === '[::1]'
    ) {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;

const WorkerApplicationPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [companies, setCompanies] = useState([]);
  const [application, setApplication] = useState(null);
  const [eligible, setEligible] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [form, setForm] = useState({
    requested_company_id: '',
    professional_email: '',
    phone_number: '',
    years_experience: '',
    specialties: '',
    portfolio_url: '',
    message: '',
  });

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true, state: { from: '/worker-application' } });
      return;
    }

    const load = async () => {
      try {
        setIsLoading(true);
        const [companiesResponse, meResponse] = await Promise.all([
          axios.get(apiUrl('userprofiles/worker-applications/companies/'), authHeaders),
          axios.get(apiUrl('userprofiles/worker-applications/me/'), authHeaders),
        ]);

        const companyRows = Array.isArray(companiesResponse.data) ? companiesResponse.data : [];
        const mePayload = meResponse.data || {};

        setCompanies(companyRows);
        setApplication(mePayload.application || null);
        setEligible(Boolean(mePayload.eligible));
        if (companyRows.length && !form.requested_company_id) {
          setForm((prev) => ({ ...prev, requested_company_id: String(companyRows[0].id) }));
        }
      } catch (error) {
        setFeedback({ type: 'error', message: 'Unable to load worker application data.' });
      } finally {
        setIsLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setFeedback({ type: '', message: '' });
      setIsSubmitting(true);
      const payload = {
        ...form,
        requested_company_id: Number(form.requested_company_id),
        years_experience: form.years_experience === '' ? null : Number(form.years_experience),
      };
      const response = await axios.post(
        apiUrl('userprofiles/worker-applications/submit/'),
        payload,
        authHeaders,
      );
      setApplication(response.data);
      setFeedback({ type: 'success', message: 'Worker application submitted successfully.' });
    } catch (error) {
      const apiError = error?.response?.data?.error;
      setFeedback({
        type: 'error',
        message: (typeof apiError === 'string' && apiError.trim())
          ? apiError
          : 'Unable to submit worker application right now.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FB] px-3 py-4 sm:px-4 sm:py-10">
      <div className="mx-auto max-w-3xl space-y-6">
        <header className="rounded-2xl bg-gradient-to-r from-[#0F4C81] via-[#1D6FA8] to-[#2D8CCB] px-4 py-6 text-white shadow-xl sm:px-6 sm:py-8">
          <h1 className="text-2xl font-bold sm:text-3xl">Worker Application</h1>
          <p className="mt-2 text-sm text-blue-100">
            Apply to join a company and start receiving bookings through Scheduled Beauty.
          </p>
        </header>

        <div className="space-y-4 rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          {feedback.message ? <InlineFeedback type={feedback.type || 'info'} message={feedback.message} /> : null}
          {isLoading ? <InlineFeedback type="info" message="Loading application status..." /> : null}

          {!isLoading && application ? (
            <div className="rounded-xl border border-[#D8E7F7] bg-[#F5FAFF] p-4 text-sm space-y-1">
              <p><strong>Status:</strong> {application.status}</p>
              <p><strong>Requested Company:</strong> {application.requested_company_name}</p>
              <p><strong>Professional Email:</strong> {application.professional_email}</p>
              {application.review_note ? <p><strong>Reviewer Note:</strong> {application.review_note}</p> : null}
            </div>
          ) : null}

          {!isLoading && !eligible ? (
            <InlineFeedback type="warning" message="Only client users can submit worker applications." />
          ) : null}

          {!isLoading && eligible && (!application || application.status === 'rejected') ? (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label htmlFor="requested_company_id" className="text-sm font-semibold text-gray-700">Choose Owner / Company</label>
                <select
                  id="requested_company_id"
                  required
                  value={form.requested_company_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, requested_company_id: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="professional_email" className="text-sm font-semibold text-gray-700">Professional Email</label>
                  <input
                    id="professional_email"
                    type="email"
                    required
                    value={form.professional_email}
                    onChange={(e) => setForm((prev) => ({ ...prev, professional_email: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="phone_number" className="text-sm font-semibold text-gray-700">Phone Number</label>
                  <input
                    id="phone_number"
                    type="text"
                    value={form.phone_number}
                    onChange={(e) => setForm((prev) => ({ ...prev, phone_number: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="years_experience" className="text-sm font-semibold text-gray-700">Years of Experience</label>
                  <input
                    id="years_experience"
                    type="number"
                    min={0}
                    max={80}
                    value={form.years_experience}
                    onChange={(e) => setForm((prev) => ({ ...prev, years_experience: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="portfolio_url" className="text-sm font-semibold text-gray-700">Portfolio URL</label>
                  <input
                    id="portfolio_url"
                    type="url"
                    value={form.portfolio_url}
                    onChange={(e) => setForm((prev) => ({ ...prev, portfolio_url: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="specialties" className="text-sm font-semibold text-gray-700">Specialties</label>
                <input
                  id="specialties"
                  type="text"
                  value={form.specialties}
                  onChange={(e) => setForm((prev) => ({ ...prev, specialties: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Braids, color, loc maintenance..."
                />
              </div>

              <div>
                <label htmlFor="message" className="text-sm font-semibold text-gray-700">Application Note</label>
                <textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  rows={4}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !companies.length}
                className="rounded-full bg-[#0F4C81] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0B3B65] disabled:opacity-60"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Worker Application'}
              </button>
            </form>
          ) : null}

          <div className="pt-2 text-sm flex flex-wrap gap-4">
            <Link to="/profile" className="text-[#0F4C81] hover:underline">Back to profile</Link>
            <Link to="/owner-application" className="text-[#0F4C81] hover:underline">Apply as business owner instead</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkerApplicationPage;
